import { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { toast } from 'sonner';
import { isPushSupported, getPushState, enablePush } from '@/utils/pushNotifications';

const DISMISS_KEY = 'apisai_push_onboarding_dismissed';

const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator?.standalone === true);

/**
 * PushOnboarding — nudge „Włącz powiadomienia" pokazywany PO INSTALACJI (tryb standalone).
 * Domyka pętlę powracalności: zainstaluj → włącz powiadomienia → nie przegap zapytań.
 * Pokazywany raz (dismiss w localStorage), gdy permission==='default' i push wspierany.
 */
export default function PushOnboarding() {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!isStandalone() || !isPushSupported()) return;
        if (localStorage.getItem(DISMISS_KEY) === '1') return;
        const st = await getPushState();
        if (!cancelled && st.supported && st.permission === 'default' && !st.subscribed) setShow(true);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!show) return null;

  const handleEnable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await enablePush();
      toast.success('Powiadomienia włączone — nie przegapisz żadnego zapytania!');
      setShow(false);
    } catch (e) {
      const msg = String(e?.message || '');
      if (msg === 'DENIED') {
        toast.error('Powiadomienia zablokowane. Możesz je włączyć w ustawieniach przeglądarki.');
        setShow(false);
      } else if (msg === 'VAPID_NOT_CONFIGURED') {
        toast.error('Powiadomienia są chwilowo niedostępne.');
      } else {
        toast.error('Nie udało się włączyć powiadomień. Spróbuj ponownie.');
      }
    } finally { setBusy(false); }
  };

  const handleDismiss = () => {
    setShow(false);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* noop */ }
  };

  return (
    <div
      className="mx-3 mt-3 rounded-xl bg-white border border-[var(--apisai-primary)]/30 px-3 py-2.5 flex items-center gap-3 shadow-sm"
      data-testid="push-onboarding"
    >
      <div className="w-8 h-8 rounded-lg bg-[var(--apisai-primary)]/10 flex items-center justify-center flex-shrink-0">
        <Bell className="w-4 h-4 text-[var(--apisai-primary)]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-bold text-zinc-900 leading-tight">Włącz powiadomienia</p>
        <p className="text-[11px] text-zinc-500 leading-tight truncate">Nie przegap żadnego zapytania od klienta</p>
      </div>
      <button
        onClick={handleEnable}
        disabled={busy}
        data-testid="push-onboarding-enable"
        className="flex-shrink-0 text-[12px] font-bold bg-[var(--apisai-primary)] text-white rounded-lg px-3 py-1.5 hover:brightness-95 disabled:opacity-50 transition-all"
      >
        {busy ? 'Włączam…' : 'Włącz'}
      </button>
      <button onClick={handleDismiss} data-testid="push-onboarding-dismiss" aria-label="Zamknij" className="flex-shrink-0 text-zinc-400 hover:text-zinc-600">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
